import { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";
import { applyBreakpoint } from "~/utils/Helpers";

const FONT_SIZE_CLASSES = {
    "lg": "text-[16px] leading-[20px]",
    "md": "text-[14px] leading-[20px]",
    "sm": "text-[12px] leading-[16px]",
    "xs": "text-[10px] leading-[14px]",
} as const;

const overlineVariants = cva("font-suisseBP leading-none uppercase tracking-[1px]", {
    variants: {
        size: FONT_SIZE_CLASSES,
        weight: {
            "400": "font-normal",
            "500": "font-medium",
        },
        style: {
            "normal": "",
            "italic": "italic",
        },
    },
    defaultVariants: {
        size: "md",
        weight: "400",
        style: "normal",
    },
});

type ResponsiveSize = {
    sm?: VariantProps<typeof overlineVariants>["size"];
    md?: VariantProps<typeof overlineVariants>["size"];
    lg?: VariantProps<typeof overlineVariants>["size"];
    xl?: VariantProps<typeof overlineVariants>["size"];
};

type Props = VariantProps<typeof overlineVariants> & {
    children?: ReactNode;
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "sub" | "sup" | "i" | "em";
    className?: string;
    sizes?: ResponsiveSize;
}

export const Overline = ({ children, as: Tag = "p", className, size, sizes = {}, weight, style }: Props) => {
    const responsiveClasses = Object.entries(sizes).map(([bp, variant]) => {
        return applyBreakpoint(bp, [FONT_SIZE_CLASSES[variant!]]);
    }).join(" ");

    return (
        <Tag className={cn(overlineVariants({ className, size, weight, style }), responsiveClasses)}>{children}</Tag>
    );
};

export default Overline;